import { IndicatorSeries, utils, type IndicatorTemplate, type KLineData } from 'klinecharts';

interface SarResult {
  sar?: number;
}

// klinecharts@9.8.12's built-in SAR resets the acceleration factor to 0 (not
// startAf) specifically on a downtrend→uptrend reversal — asymmetric with the
// uptrend→downtrend branch, which correctly resets to startAf. Confirmed fixed
// upstream (both branches reset to startAf) but only in klinecharts v10 (a
// breaking major this app hasn't adopted). Reimplement with the symmetric
// reset and re-register under the same 'SAR' name to override the built-in.
export const sar: IndicatorTemplate<SarResult> = {
  name: 'SAR',
  shortName: 'SAR',
  series: IndicatorSeries.Price,
  calcParams: [2, 2, 20],
  precision: 2,
  shouldOhlc: true,
  figures: [
    {
      key: 'sar',
      title: 'SAR: ',
      type: 'circle',
      styles: (data, indicator, defaultStyles) => {
        const current = data.current;
        const sarValue = current.indicatorData?.sar ?? Number.MIN_SAFE_INTEGER;
        const halfHL = ((current.kLineData?.high ?? 0) + (current.kLineData?.low ?? 0)) / 2;
        const color = sarValue < halfHL
          ? (utils.formatValue(indicator.styles, 'circles[0].upColor', defaultStyles.circles[0].upColor) as string)
          : (utils.formatValue(indicator.styles, 'circles[0].downColor', defaultStyles.circles[0].downColor) as string);
        return { color };
      },
    },
  ],
  calc: (dataList: KLineData[], indicator) => {
    const params = indicator.calcParams;
    const startAf = params[0] / 100;
    const step = params[1] / 100;
    const maxAf = params[2] / 100;

    let af = startAf;
    let ep = -100;
    let isIncreasing = false;
    let sarValue = 0;

    return dataList.map((kLineData, i) => {
      const preSar = sarValue;
      const high = kLineData.high;
      const low = kLineData.low;
      if (isIncreasing) {
        if (ep === -100 || ep < high) {
          ep = high;
          af = Math.min(af + step, maxAf);
        }
        sarValue = preSar + af * (ep - preSar);
        const lowMin = Math.min(dataList[Math.max(1, i) - 1].low, low);
        if (sarValue > kLineData.low) {
          sarValue = ep;
          af = startAf;
          ep = -100;
          isIncreasing = !isIncreasing;
        } else if (sarValue > lowMin) {
          sarValue = lowMin;
        }
      } else {
        if (ep === -100 || ep > low) {
          ep = low;
          af = Math.min(af + step, maxAf);
        }
        sarValue = preSar + af * (ep - preSar);
        const highMax = Math.max(dataList[Math.max(1, i) - 1].high, high);
        if (sarValue < kLineData.high) {
          sarValue = ep;
          af = startAf; // fixed: bundled 9.8.12 built-in incorrectly resets this to 0
          ep = -100;
          isIncreasing = !isIncreasing;
        } else if (sarValue < highMax) {
          sarValue = highMax;
        }
      }
      return { sar: sarValue };
    });
  },
};

export default sar;
